/**
 * Shared style utilities for card components
 * Provides consistent styling across Aspiration, Goal, and List cards
 */

/**
 * Priority badge styles - consistent across all entity types
 * Priority 1 = highest/most urgent (red), Priority 5 = lowest (blue)
 */
export const PRIORITY_STYLES = {
  1: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  2: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
  4: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
  5: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
} as const;

/**
 * Get the style class for a priority level
 * @param priority - Priority number (1-5)
 * @returns Tailwind class string
 */
export function getPriorityStyle(priority: number): string {
  return PRIORITY_STYLES[priority as keyof typeof PRIORITY_STYLES] ?? PRIORITY_STYLES[5];
}

/**
 * Status badge styles - unified across entities
 */
export const STATUS_STYLES = {
  not_started: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  complete: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  on_hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  blocked: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  paused: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
} as const;

/**
 * Get the style class for a status
 * @param status - Status string
 * @returns Tailwind class string
 */
export function getStatusStyle(status: string): string {
  return STATUS_STYLES[status as keyof typeof STATUS_STYLES] ?? STATUS_STYLES.not_started;
}

/**
 * Category badge styles (primarily for aspirations)
 */
export const CATEGORY_STYLES = {
  career: 'bg-purple-100 text-purple-800',
  financial: 'bg-emerald-100 text-emerald-800',
  health: 'bg-red-100 text-red-800',
  education: 'bg-blue-100 text-blue-800',
  personal: 'bg-indigo-100 text-indigo-800',
  family: 'bg-pink-100 text-pink-800',
  travel: 'bg-teal-100 text-teal-800',
  social: 'bg-orange-100 text-orange-800',
  spiritual: 'bg-violet-100 text-violet-800',
} as const;

/**
 * Get the style class for a category
 * @param category - Category string
 * @returns Tailwind class string
 */
export function getCategoryStyle(category: string): string {
  const key = category.toLowerCase() as keyof typeof CATEGORY_STYLES;
  return CATEGORY_STYLES[key] ?? 'bg-gray-100 text-gray-800';
}
